import { useState, useEffect, useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, getStatusDisplay, normalizeStatus } from "@/components/ui/status-badge";
import { PolicyLibraryCard } from "@/components/policy/ResponsivePolicyCard";
import { PolicyCardSkeleton } from "@/components/ui/policy-card-skeleton";
import { TableRowSkeleton } from "@/components/ui/table-skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileText, Calendar, Users, Eye, LayoutGrid, Table as TableIcon, RefreshCw, Edit, Send, XCircle, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { Link, useLocation } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchDepartments, Department } from "@/lib/department-utils";
import { AppDataTable } from "@/components/ui/app-data-table";
import { formatFramework, useCompliancePrograms } from "@/hooks/use-compliance-programs";

interface Policy {
  id: string;
  title: string;
  department: string | null;
  description: string | null;
  version: number;
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  updated_at: string;
  effective_date: string | null;
  next_renewal_date: string | null;
  creator: {
    name: string;
  } | null;
  program_id: string | null;
  program?: {
    id: string;
    name: string;
    framework: string;
  } | null;
}

const PolicyLibrary = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [programFilter, setProgramFilter] = useState("all");
  const [viewMode, setViewMode] = useState<'grid' | 'table'>("grid");
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'send_for_approval' | null>(null);
  const [actionComments, setActionComments] = useState("");

  const { profile, hasAnyRole, roles } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const { programs } = useCompliancePrograms();

  // Fetch policies on component mount
  useEffect(() => {
    if (profile?.company_id) {
      fetchPolicies();
    }
  }, [profile?.company_id]);

  // Fetch departments
  useEffect(() => {
    if (profile?.company_id) {
      fetchDepartments(profile.company_id)
        .then(setDepartments)
        .catch(console.error);
    }
  }, [profile?.company_id]);

  // Refresh policies when navigating back to this page (e.g., after deleting a policy)
  useEffect(() => {
    if (profile?.company_id && location.pathname === '/policy-library') {
      // Check if we have a refresh signal from navigation
      if (location.state?.refresh) {
        fetchPolicies(true);
        // Clear the refresh signal to prevent unnecessary re-fetches
        window.history.replaceState({}, document.title);
      } else {
        fetchPolicies();
      }
    }
  }, [location.pathname, location.state, profile?.company_id]);

  const fetchPolicies = async (isRefresh = false) => {
    if (!profile?.company_id) return;
    
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      let query = (supabase as any)
        .from('policies')
        .select('*, program:programs(id,name,framework)')
        .eq('company_id', profile.company_id);

      // Apply role-based filtering
      if (hasAnyRole(['admin', 'creator'])) {
        // Admins and creators can see all policies
        query = query;
      } else if (hasAnyRole(['reviewer', 'approver'])) {
        // Reviewers and approvers can only see:
        // 1) All approved policies
        // 2) Policies assigned to them that are NOT in draft
        // (Drafts should only be visible to creators and admins)
        query = query.or(
          `status.eq.approved,` +
          `and(status.neq.draft,assigned_reviewers.cs.{${profile.id}}),` +
          `and(status.neq.draft,assigned_approvers.cs.{${profile.id}})`
        );
      } else {
        // Default users can only see approved policies
        query = query.eq('status', 'approved');
      }

      const { data: policiesData, error: policiesError } = await query.order('created_at', { ascending: false });

      if (policiesError) throw policiesError;

      if (policiesData && policiesData.length > 0) {
        // Get unique creator IDs
        const creatorIds = [...new Set(policiesData.map(policy => policy.created_by))];
        
        // Fetch creator profiles
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', creatorIds);

        if (profilesError) throw profilesError;

        // Combine policies with creator information
        const policiesWithCreators = policiesData.map(policy => ({
          ...policy,
          creator: profilesData?.find(profile => profile.id === policy.created_by) || null
        }));

        setPolicies(policiesWithCreators);
      } else {
        setPolicies([]);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast({
        title: "Error",
        description: "Failed to fetch policies",
        variant: "destructive"
      });
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
  };

  const getDepartmentDisplay = (department: string | null) => {
    if (!department) return 'General';
    return department;
  };

  // Map display labels to database status values
  const getStatusValue = (displayLabel: string) => {
    const statusMap: Record<string, string> = {
      "All Status": "all",
      "Approved": "approved",
      "Under Review": "under_review",
      "Draft": "draft",
      "Pending Approval": "pending_approval",
      "Rejected": "rejected"
    };
    return statusMap[displayLabel] || displayLabel;
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (policy.description && policy.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = departmentFilter === "all" || policy.department === departmentFilter;
    const matchesProgram = programFilter === "all" || policy.program_id === programFilter;
    
    // Convert display label to database status value for comparison
    const statusValue = getStatusValue(statusFilter);
    const matchesStatus = statusValue === "all" || policy.status === statusValue;
    
    return matchesSearch && matchesDepartment && matchesStatus && matchesProgram;
  });

  const policyColumns = useMemo<ColumnDef<Policy>[]>(() => [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
    },
    {
      accessorKey: "department",
      header: "Department",
      cell: ({ row }) => <span className="text-muted-foreground">{getDepartmentDisplay(row.original.department)}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusBadge status={normalizeStatus(row.original.status) as any}>
          {getStatusDisplay(normalizeStatus(row.original.status))}
        </StatusBadge>
      ),
    },
    {
      id: "program",
      header: "Program",
      cell: ({ row }) => (
        row.original.program ? (
          <div className="text-sm">
            <span className="font-medium">{formatFramework(row.original.program.framework)}</span>
            <span className="text-muted-foreground"> · {row.original.program.name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">None</span>
        )
      ),
      enableSorting: false,
    },
    {
      accessorKey: "version",
      header: "Version",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.version}</span>,
    },
    {
      accessorKey: "effective_date",
      header: "Effective",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.effective_date ? format(new Date(row.original.effective_date), "MMM d, yyyy") : "Not set"}
        </span>
      ),
    },
    {
      accessorKey: "next_renewal_date",
      header: "Renewal",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.next_renewal_date ? format(new Date(row.original.next_renewal_date), "MMM d, yyyy") : "Not set"}
        </span>
      ),
    },
    {
      id: "creator",
      header: "Creator",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.creator?.name || "Unknown"}</span>,
      enableSorting: false,
    },
    {
      id: "action",
      header: "Action",
      cell: ({ row }) => (
        <div className="flex gap-2 justify-end">
          <Link to={`/policy/${row.original.id}`}>
            <Button size="sm" variant="outline">
              <Eye className="w-3 h-3 mr-1" />
              View
            </Button>
          </Link>
          {row.original.status === "draft" && (
            <Link to={`/policy-creator?edit=${row.original.id}`}>
              <Button size="sm">
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
            </Link>
          )}
        </div>
      ),
      meta: { headerClassName: "text-right", cellClassName: "text-right" },
      enableSorting: false,
    },
  ], []);

  // Workflow action handlers
  const handleSendForApproval = (policy: Policy) => {
    setSelectedPolicy(policy);
    setActionType('send_for_approval');
    setActionDialogOpen(true);
  };

  const handleApprove = (policy: Policy) => {
    setSelectedPolicy(policy);
    setActionType('approve');
    setActionDialogOpen(true);
  };

  const handleReject = (policy: Policy) => {
    setSelectedPolicy(policy);
    setActionType('reject');
    setActionDialogOpen(true);
  };

  const handleWorkflowAction = async () => {
    if (!selectedPolicy || !profile?.company_id) return;
    
    try {
      let newStatus = '';
      let workflowAction = '';
      
      switch (actionType) {
        case 'send_for_approval':
          newStatus = 'pending_approval';
          workflowAction = 'reviewed';
          break;
        case 'approve':
          newStatus = 'approved';
          workflowAction = 'approved';
          break;
        case 'reject':
          newStatus = 'rejected';
          workflowAction = 'rejected';
          break;
      }

      // Update policy status
      const { error: policyError } = await supabase
        .from('policies')
        .update({ status: newStatus as any })
        .eq('id', selectedPolicy.id);

      if (policyError) throw policyError;

      // Create workflow entry
      await supabase
        .from('policy_workflow')
        .insert({
          policy_id: selectedPolicy.id,
          status: newStatus,
          assigned_to: profile.id,
          action: workflowAction,
          previous_status: selectedPolicy.status,
          new_status: newStatus,
          created_by: profile.id,
          company_id: profile.company_id,
          comments: actionComments || null
        });

      // Add comment if provided
      if (actionComments) {
        await supabase
          .from('policy_comments')
          .insert({
            policy_id: selectedPolicy.id,
            company_id: profile.company_id,
            created_by: profile.id,
            content: actionComments
          });
      }

      toast({
        title: "Success",
        description: `Policy ${workflowAction} successfully`,
      });

      setActionDialogOpen(false);
      setActionComments('');
      fetchPolicies(true);
    } catch (error: any) {
      console.error('Error performing action:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to perform action",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-medium text-foreground">Policy Library</h1>
          <p className="text-muted-foreground">
            Manage and organize all your policies in one central location.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchPolicies(true)}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search policies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Status">All Status</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Pending Approval">Pending Approval</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>

              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={programFilter} onValueChange={setProgramFilter}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={program.id}>
                      {formatFramework(program.framework)} · {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('grid')}>
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button variant={viewMode === 'table' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('table')}>
                <TableIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <PolicyCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <Card className="shadow-card">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Effective</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead>Creator</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableRowSkeleton rows={8} columns={9} />
              </Table>
            </CardContent>
          </Card>
        )
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPolicies.map((policy) => (
            <PolicyLibraryCard
              key={policy.id}
              policy={policy}
              userRoles={roles || []}
              onEditDraft={(policyId) => window.location.href = `/policy-creator?edit=${policyId}`}
              onSendForApproval={handleSendForApproval}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      ) : (
        <Card className="shadow-card">
          <CardContent className="p-0">
            <AppDataTable
              columns={policyColumns}
              data={filteredPolicies}
              emptyMessage="No policies found."
              getRowId={(policy) => policy.id}
            />
          </CardContent>
        </Card>
      )}

      {filteredPolicies.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-muted-foreground mb-2">No policies found</h3>
          <p className="text-sm text-muted-foreground">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Workflow Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'send_for_approval' && 'Send for Approval'}
              {actionType === 'approve' && 'Approve Policy'}
              {actionType === 'reject' && 'Reject Policy'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'send_for_approval' && 'Add any comments before sending this policy for approval (optional).'}
              {actionType === 'approve' && 'Add any comments before approving this policy (optional).'}
              {actionType === 'reject' && 'Please provide a reason for rejecting this policy (required).'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">
                Comments {actionType === 'reject' && '(Required)'}
              </label>
              <Textarea
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder={actionType === 'reject' 
                  ? 'Enter the reason for rejection...' 
                  : 'Add any comments...'}
                rows={4}
                required={actionType === 'reject'}
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setActionDialogOpen(false);
                setActionComments('');
                setActionType(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleWorkflowAction}
                variant={actionType === 'reject' ? 'destructive' : 'default'}
              >
                {actionType === 'send_for_approval' && 'Send for Approval'}
                {actionType === 'approve' && 'Approve Policy'}
                {actionType === 'reject' && 'Reject Policy'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolicyLibrary;
